import { ReportRequestState } from './types';

export type StepId =
  | 'type'
  | 'project'
  | 'source'
  | 'demographics'
  | 'competitiveSet'
  | 'parent'
  | 'optimization'
  | 'mixAndMatch'
  | 'options';

export interface StepDefinition {
  id: StepId;
  label: string;
  description?: string;
}

const STEP_LABELS: Record<StepId, string> = {
  type: 'Report type',
  project: 'Project',
  source: 'Source report',
  demographics: 'Demographics',
  competitiveSet: 'Competitive set',
  parent: 'Parent report',
  optimization: 'Optimization target',
  mixAndMatch: 'Versions',
  options: 'Options',
};

const step = (id: StepId, description?: string): StepDefinition => ({
  id,
  label: STEP_LABELS[id],
  description,
});

export interface StepContext {
  isSuperAdmin: boolean;
}

/**
 * The ordered sections of the form for the current report kind. Derived, never
 * mutated, so switching report kind simply produces a different list.
 */
export const getSteps = (
  state: Pick<ReportRequestState, 'mode' | 'reportKind' | 'updateSource'>,
  { isSuperAdmin }: StepContext,
): StepDefinition[] => {
  if (state.mode === 'update') {
    // Without a source report there is nothing to add products to.
    if (!state.updateSource) return [step('source', 'What is being updated')];
    return [
      step('source', 'What is being updated'),
      step('competitiveSet', 'Add products'),
      step('options'),
    ];
  }

  // The chosen kind is already the page subtitle, so the section carries no description.
  const typeStep = step('type');

  switch (state.reportKind) {
    case 'market_survey':
      return [
        typeStep,
        step('project'),
        step('competitiveSet'),
        step('demographics'),
        step('options'),
      ];
    case 'optimization':
      return [
        typeStep,
        step('parent'),
        step('optimization'),
        step('options'),
      ];
    case 'mix_and_match':
      return [
        typeStep,
        step('parent'),
        step('mixAndMatch'),
        ...(isSuperAdmin ? [step('options')] : []),
      ];
    default:
      return [typeStep];
  }
};
